import type {
    RibbonControlGroupDefinitionMLR,
    RibbonControlGroupDefinitionSLR,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components';
import { Multiline, SingleLine, getKeytipString, constructKeytip } from 'owa-command-ribbon';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import CommandRibbonStrings from 'owa-locstrings/lib/strings/commandRibbon.locstring.json';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { RibbonTabId, RibbonGroupId } from 'owa-ribbon-ids';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';

/**
 * Helper function to be used for creating ribbon groups in MLR.
 * @param groupId is the RibbonGroupId for this group. Must be unique, @see {RibbonGroupId}.
 * @param popoutComposeViewState must be provided if this is a popout compose ribbon
 * @returns RibbonControlGroupDefinitionSLR object. Searchable in 1JS Repo, in `Ribbon.Props.ts`
 */
export const createRibbonGroupMLR = (
    tabId: RibbonTabId,
    groupId: RibbonGroupId,
    popoutComposeViewState?: ComposeViewState
): RibbonControlGroupDefinitionMLR => {
    return {
        type: Multiline,
        id: getComposeRibbonId(groupId, popoutComposeViewState).toString(),
        title: '',
        controls: [],
        layouts: [],
        commonGroupFlyout: undefined,
        keytipProps: isFeatureEnabled('mon-ribbon-keytips')
            ? constructKeytip([getKeytipString(tabId)], getKeytipString(tabId))
            : undefined,
    };
};

/**
 * Helper function to be used for creating ribbon groups in SLR.
 * Importantly:
 *   - `hasOwnOverflow` should be false and
 *   - `shouldAllowAllControlsInOverflow` should be true and
 *   - keytipProps matching the tabId needs to be provided
 * Otherwise, Customizer gets into mismatched states with the Ribbon.
 * @param groupId is the RibbonGroupId for this group. Must be unique, @see {RibbonGroupId}.
 * @param popoutComposeViewState must be provided if this is a popout compose ribbon
 * @returns RibbonControlGroupDefinitionSLR object. Searchable in 1JS Repo, in `Ribbon.Props.ts`
 */
export const createRibbonGroupSLR = (
    tabId: RibbonTabId,
    groupId: RibbonGroupId,
    popoutComposeViewState?: ComposeViewState
): RibbonControlGroupDefinitionSLR => {
    return {
        type: SingleLine,
        id: getComposeRibbonId(groupId, popoutComposeViewState).toString(),
        title: '',
        overflowMenuLauncherLabel: '',
        hasOwnOverflow: false,
        shouldAllowAllControlsInOverflow: true,
        controls: [],
        keytipProps: isFeatureEnabled('mon-ribbon-keytips')
            ? constructKeytip([getKeytipString(tabId)], getKeytipString(tabId))
            : undefined,
    };
};

/**
 * Helper function to be used for creating ribbon groups.
 * As there is only one group, `hasOwnOverflow` is set to true.
 * @param groupId is the RibbonGroupId for this group. Must be unique, @see {RibbonGroupId}.
 * @param popoutComposeViewState must be provided if this is a popout compose ribbon
 * @returns RibbonControlGroupDefinitionSLR object. Searchable in 1JS Repo, in `Ribbon.Props.ts`
 */
export const createRibbonGroupToolbar = (
    tabId: RibbonTabId,
    groupId: RibbonGroupId,
    popoutComposeViewState?: ComposeViewState
): RibbonControlGroupDefinitionSLR => {
    return {
        type: SingleLine,
        id: getComposeRibbonId(groupId, popoutComposeViewState).toString(),
        title: '',
        overflowMenuLauncherLabel: '',
        hasOwnOverflow: true,
        shouldAllowAllControlsInOverflow: true,
        controls: [],
        keytipProps: isFeatureEnabled('mon-ribbon-keytips')
            ? constructKeytip([], loc(CommandRibbonStrings.overflowWellKeytip), true /*hasMenu*/)
            : undefined,
    };
};
